// Redux Saga
import { takeLatest, call, put, select } from 'redux-saga/effects';

// Services
import { facialApi } from 'services/apiDigitalTicket/facial';

// Actions
import * as DialogsActions from 'store/ducks/dialogs/actions';
import * as SnackbarsActions from 'store/ducks/snackbars/actions';

// Constants
import { errorMessagersList } from 'helpers/errorMessagers';

import * as FacialActions from './actions';
import { FacialTypes } from './types';

function* list({
  payload,
}: ReturnType<typeof FacialActions.listFacialRequest>): any {
  try {
    yield put(DialogsActions.setOpenLoading({ title: 'Carregando...' }));
    const { body } = payload;
    const transportOperator = yield select(
      (state) => state.transportOperators.transportOperatorSelected,
    );

    const response = yield call(facialApi.list, {
      params: { transportOperatorId: transportOperator.id, ...body },
    });

    if (response.status === 200) {
      yield put(
        FacialActions.listFacialSuccess({
          facialList: response.data,
          totalCount: parseInt(response?.headers.totalcount, 10),
        }),
      );
    }

    yield put(DialogsActions.setClose());
  } catch (error) {
    const internalCode: string =
      error.response && error.response.data.internalCode;
    const message = errorMessagersList(internalCode);

    console.error('facial recognition - list - error', error);

    yield put(
      SnackbarsActions.setOpen({
        open: true,
        type: 'error',
        message,
      }),
    );

    yield put(FacialActions.listFacialFailure());
    yield put(DialogsActions.setClose());
  }
}

function* lastValidationList({
  payload,
}: ReturnType<typeof FacialActions.lastValidationListFacialRequest>): any {
  try {
    const { body } = payload;
    const transportOperator = yield select(
      (state) => state.transportOperators.transportOperatorSelected,
    );

    const response = yield call(facialApi.list, {
      params: { transportOperatorId: transportOperator.id, ...body },
    });

    if (response.status === 200) {
      yield put(
        FacialActions.lastValidationListFacialSuccess({
          lastValidationFacialList: response.data,
          totalCount: parseInt(response?.data?.length, 10),
        }),
      );
    }
  } catch (error) {
    const internalCode: string =
      error.response && error.response.data.internalCode;
    const message = errorMessagersList(internalCode);

    console.error('facial recognition - list - error', error);

    yield put(
      SnackbarsActions.setOpen({
        open: true,
        type: 'error',
        message,
      }),
    );

    yield put(FacialActions.lastValidationListFacialFailure());
    yield put(DialogsActions.setClose());
  }
}

function* get({
  payload,
}: ReturnType<typeof FacialActions.getSettingsRequest>): any {
  try {
    yield put(
      DialogsActions.setOpenLoading({ title: 'Carregando configurações...' }),
    );

    const transportOperator = yield select(
      (state) => state.transportOperators.transportOperatorSelected,
    );

    const response = yield call(facialApi.get, {
      params: { transportOperatorId: transportOperator.id, ...payload },
    });

    if (response.status === 200) {
      yield put(
        FacialActions.getSettingsSuccess({
          facialSettingsData: response.data,
        }),
      );

      yield put(DialogsActions.setClose());
    }
  } catch (error) {
    const internalCode: string =
      error.response && error.response.data.internalCode;
    const message = errorMessagersList(internalCode);

    console.error('facial recognition - get settings - error', error);

    yield put(
      SnackbarsActions.setOpen({
        open: true,
        type: 'error',
        message,
      }),
    );

    yield put(FacialActions.getSettingsFailure());
    yield put(DialogsActions.setClose());
  }
}

function* listDailyResume({
  payload,
}: ReturnType<typeof FacialActions.listFacialDailyResumeRequest>): any {
  try {
    yield put(DialogsActions.setOpenLoading({ title: 'Carregando...' }));

    const transportOperator = yield select(
      (state) => state.transportOperators.transportOperatorSelected,
    );

    const response = yield call(facialApi.listDailyResume, {
      params: { transportOperatorId: transportOperator.id, ...payload },
    });

    if (response.status === 200) {
      yield put(
        FacialActions.listFacialDailyResumeSuccess({
          facialDailyResumeList: response.data,
          totalCount: parseInt(response?.data?.length, 10),
        }),
      );

      yield put(DialogsActions.setClose());
    }
  } catch (error) {
    const internalCode: string =
      error.response && error.response.data.internalCode;
    const message = errorMessagersList(internalCode);

    console.error('facial recognition - list - error', error);

    yield put(
      SnackbarsActions.setOpen({
        open: true,
        type: 'error',
        message,
      }),
    );

    yield put(FacialActions.listFacialDailyResumeFailure());
    yield put(DialogsActions.setClose());
  }
}

function* listDayDetail({
  payload,
}: ReturnType<typeof FacialActions.listFacialDayDetailRequest>): any {
  try {
    yield put(DialogsActions.setOpenLoading({ title: 'Carregando...' }));

    const transportOperator = yield select(
      (state) => state.transportOperators.transportOperatorSelected,
    );

    const response = yield call(facialApi.listDayDetail, {
      params: { transportOperatorId: transportOperator.id, ...payload },
    });

    if (response.status === 200) {
      yield put(
        FacialActions.listFacialDayDetailSuccess({
          facialDayDetailList: response.data,
          totalCount: parseInt(response?.data?.length, 10),
        }),
      );

      yield put(DialogsActions.setClose());
    }
  } catch (error) {
    const internalCode: string =
      error.response && error.response.data.internalCode;
    const message = errorMessagersList(internalCode);

    console.error('facial recognition - list - error', error);

    yield put(
      SnackbarsActions.setOpen({
        open: true,
        type: 'error',
        message,
      }),
    );

    yield put(FacialActions.listFacialDayDetailFailure());
    yield put(DialogsActions.setClose());
  }
}

function* updateSettings({
  payload,
}: ReturnType<typeof FacialActions.updateSettingsRequest>): any {
  try {
    yield put(
      DialogsActions.setOpenLoading({ title: 'Atualizando configurações...' }),
    );

    const transportOperator = yield select(
      (state) => state.transportOperators.transportOperatorSelected,
    );

    const response = yield call(facialApi.updateSettings, {
      data: { transportOperatorId: transportOperator.id, ...payload },
    });

    if (response.status === 200) {
      yield put(
        FacialActions.updateSettingsSuccess({
          facialSettingsData: response.data,
        }),
      );

      yield put(DialogsActions.setClose());

      yield put(FacialActions.getSettingsRequest({}));
    }
  } catch (error) {
    const internalCode: string =
      error.response && error.response.data.internalCode;
    const message = errorMessagersList(internalCode);

    console.error('facial recognition - update settings - error', error);

    yield put(
      SnackbarsActions.setOpen({
        open: true,
        type: 'error',
        message,
      }),
    );

    yield put(FacialActions.updateSettingsFailure());
    yield put(DialogsActions.setClose());
  }
}

function* uploadStatus({
  payload,
}: ReturnType<typeof FacialActions.getUploadStatusRequest>): any {
  try {
    yield put(DialogsActions.setSecondOpenLoading({ title: 'Carregando...' }));

    const transportOperator = yield select(
      (state) => state.transportOperators.transportOperatorSelected,
    );

    const response = yield call(facialApi.getUploadStatus, {
      params: { transportOperatorId: transportOperator.id, ...payload },
    });

    if (response.status === 200) {
      yield put(
        FacialActions.getUploadStatusSuccess({
          uploadStatusList: response.data,
        }),
      );
    }

    yield put(DialogsActions.setSecondClose());
  } catch (error) {
    const internalCode: string =
      error.response && error.response.data.internalCode;
    const message = errorMessagersList(internalCode);

    console.error('facial recognition - upload status - error', error);

    yield put(
      SnackbarsActions.setOpen({
        open: true,
        type: 'error',
        message,
      }),
    );

    yield put(FacialActions.getUploadStatusFailure());
    yield put(DialogsActions.setSecondClose());
  }
}

function* userStatus({
  payload,
}: ReturnType<typeof FacialActions.getUserStatusRequest>): any {
  try {
    const transportOperator = yield select(
      (state) => state.transportOperators.transportOperatorSelected,
    );

    const response = yield call(facialApi.getUserStatus, {
      params: { transportOperatorId: transportOperator.id, ...payload },
    });

    if (response.status === 200) {
      yield put(
        FacialActions.getUserStatusSuccess({
          userStatus: response.data,
        }),
      );

      yield put(DialogsActions.setClose());
    }
  } catch (error) {
    console.error('facial recognition - get user status - error', error);

    yield put(FacialActions.getUserStatusFailure());
    yield put(DialogsActions.setClose());
  }
}

function* approvalStatus({
  payload,
  meta,
}: ReturnType<typeof FacialActions.updateApprovalStatusRequest>): any {
  try {
    yield put(
      DialogsActions.setOpenLoading({ title: 'Atualizando status...' }),
    );

    const transportOperator = yield select(
      (state) => state.transportOperators.transportOperatorSelected,
    );

    const response = yield call(facialApi.updateApprovalStatus, {
      data: { transportOperatorId: transportOperator.id, ...payload },
    });

    if (response.status === 200) {
      yield put(FacialActions.updateApprovalStatusSuccess());
      yield put(
        FacialActions.setSelectedApprovalStatus({
          selectedApprovalStatus: response.data.approvalStatus,
        }),
      );

      yield put(DialogsActions.setClose());
      yield meta.promise.resolve(response);

      if (response.data.userId !== null) {
        yield put(
          FacialActions.getUserStatusRequest({
            userId: response.data.userId,
          }),
        );
      }
    }
  } catch (error) {
    const internalCode: string =
      error.response && error.response.data.internalCode;
    const message = errorMessagersList(internalCode);

    console.error('facial recognition - update approval status - error', error);

    yield put(
      SnackbarsActions.setOpen({
        open: true,
        type: 'error',
        message,
      }),
    );

    yield put(FacialActions.updateApprovalStatusFailure());
    yield meta.promise.reject(error);
    yield put(DialogsActions.setClose());
  }
}

// eslint-disable-next-line import/prefer-default-export
export const facialSagas = [
  takeLatest(FacialTypes.LIST_FACIAL_REQUEST, list),
  takeLatest(FacialTypes.GET_SETTINGS_REQUEST, get),
  takeLatest(FacialTypes.LIST_FACIAL_DAILY_RESUME_REQUEST, listDailyResume),
  takeLatest(FacialTypes.LIST_FACIAL_DAY_DETAIL_REQUEST, listDayDetail),
  takeLatest(FacialTypes.GET_UPLOAD_STATUS_REQUEST, uploadStatus),
  takeLatest(FacialTypes.UPDATE_SETTINGS_REQUEST, updateSettings),
  takeLatest(FacialTypes.GET_USER_STATUS_REQUEST, userStatus),
  takeLatest(FacialTypes.UPDATE_APPROVAL_STATUS_REQUEST, approvalStatus),
  takeLatest(
    FacialTypes.LAST_VALIDATION_LIST_FACIAL_REQUEST,
    lastValidationList,
  ),
];
